/**
 * Customer and allocation chips, plus the test-rig toggle. The chips list the
 * customers the vocabulary knows and any the response carries; a count shows
 * when the response is unfiltered and so knows it.
 */
import React from "react";
import { styled } from "styled-components";
import { FleetQuery } from "./query";
import { allocationLabel, customerLabel } from "./labels";
import { FleetResponse } from "./types";
import { tokens } from "../TractorState/tokens";

interface FleetFiltersProps {
  query: FleetQuery;
  fleet: FleetResponse | null;
  onChange: (next: FleetQuery) => void;
}

const KNOWN_CUSTOMERS = ["delta", "aerovect"];
const KNOWN_ALLOCATIONS = ["OPS", "ENG"];

const FleetFilters: React.FC<FleetFiltersProps> = ({
  query,
  fleet,
  onChange,
}) => {
  const customers = Array.from(
    new Set([
      ...KNOWN_CUSTOMERS,
      ...(fleet?.customers.map((c) => c.customer) ?? []),
    ]),
  );
  const allocations = Array.from(
    new Set([
      ...KNOWN_ALLOCATIONS,
      ...(fleet?.vehicles
        .map((v) => v.allocation)
        .filter((a): a is string => !!a) ?? []),
    ]),
  );
  const counts: Record<string, number> = {};
  if (fleet && !query.customer && !query.allocation) {
    for (const c of fleet.customers) counts[c.customer] = c.tractors;
  }
  const set = (patch: Partial<FleetQuery>) => {
    const next = { ...query, ...patch };
    if (!next.customer) delete next.customer;
    if (!next.allocation) delete next.allocation;
    onChange(next);
  };
  return (
    <Row>
      <Eyebrow>Customer</Eyebrow>
      <Chip
        type="button"
        aria-pressed={!query.customer}
        onClick={() => set({ customer: undefined })}
      >
        All
        {fleet && !query.customer && !query.allocation ? (
          <small> {fleet.vehicles.length}</small>
        ) : null}
      </Chip>
      {customers.map((c) => (
        <Chip
          key={c}
          type="button"
          aria-pressed={query.customer === c}
          onClick={() => set({ customer: c })}
        >
          {customerLabel(c)}
          {counts[c] !== undefined ? <small> {counts[c]}</small> : null}
        </Chip>
      ))}
      <Divider />
      <Eyebrow>Allocation</Eyebrow>
      <Chip
        type="button"
        aria-pressed={!query.allocation}
        onClick={() => set({ allocation: undefined })}
      >
        All
      </Chip>
      {allocations.map((a) => (
        <Chip
          key={a}
          type="button"
          aria-pressed={query.allocation === a}
          onClick={() => set({ allocation: a })}
        >
          {allocationLabel(a)}
        </Chip>
      ))}
      <Spacer />
      <Rigs>
        <input
          type="checkbox"
          checked={query.rigs}
          onChange={(e) => set({ rigs: e.target.checked })}
        />
        Show test rigs
      </Rigs>
    </Row>
  );
};

export default FleetFilters;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
`;

const Eyebrow = styled.span`
  font: 500 11px ${tokens.fontMono};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.slate};
  margin-right: 2px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${tokens.inputLine};
  background: ${tokens.white};
  font: 600 12px ${tokens.fontSans};
  color: ${tokens.slate};
  cursor: pointer;
  min-height: 34px;
  small {
    font-weight: 500;
    opacity: 0.8;
  }
  &[aria-pressed="true"] {
    background: ${tokens.navy};
    border-color: ${tokens.navy};
    color: ${tokens.white};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 22px;
  background: ${tokens.line};
  margin: 0 6px;
`;

const Spacer = styled.span`
  flex: 1;
`;

const Rigs = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
  cursor: pointer;
  input {
    width: 16px;
    height: 16px;
    margin: 0;
  }
`;
